/**
 * Rock TCS — shared section styling primitives (same idiom as
 * TriaxialParametersTab: compact inputs, 2-col grids, overline headers).
 */
import { InputAdornment, Typography } from '@mui/material';
import { FormulaHeaderTooltip } from '../components/FormulaHeaderTooltip';

export const inputSx = {
  '& .MuiInputBase-input': { py: 0.6, px: 1, fontSize: '0.78rem' },
  '& .MuiInputLabel-root': { fontSize: '0.78rem' },
  '& .MuiInputLabel-shrink': { fontSize: '0.84rem' },
  '& .MuiInputBase-input::-webkit-outer-spin-button, & .MuiInputBase-input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none', margin: 0,
  },
  '& .MuiInputBase-input[type=number]': { MozAppearance: 'textfield' },
} as const;

export const fieldGridSx = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 0.5, rowGap: 1.25,
} as const;

/**
 * BUG-2026-0137 §4 — one compact row per measurement kind: all 6 diameters on
 * one row, all 4 lengths on the next (smaller boxes, natural D1→D6 / L1→L4
 * tab order). 6 equal columns so the length row lines up under the diameters.
 */
export const measurementRowSx = {
  display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', columnGap: 0.5,
} as const;

// BUG-2026-0133 — "Card lift" (Option B): give the Rock BTS + TCS section cards
// real depth instead of the flat outlined look — rounded corners, a sage-tinted
// layered shadow, and a gentle hover lift (raise + sage border). Shared by every
// Rock test card via this one const. Colour literals mirror theme tokens
// (primary #4A6741, border #C5CEC0) — kept inline because this is a static style
// object with no `theme` in scope (same idiom as inputSx above).
export const paperSx = {
  p: 1.5,
  height: '100%',
  borderRadius: '10px',
  border: '1px solid #C5CEC0',
  boxShadow: '0 1px 3px rgba(44,53,44,0.06), 0 6px 16px rgba(74,103,65,0.08)',
  transition: 'box-shadow .18s ease, transform .18s ease, border-color .18s ease',
  // Hover lift kept deliberately subtle (1px) — the original 3px jump was
  // distracting when mousing across the many cards on a test page.
  '&:hover': {
    transform: 'translateY(-1px)',
    borderColor: '#4A6741',
    boxShadow: '0 2px 5px rgba(44,53,44,0.07), 0 8px 18px rgba(74,103,65,0.10)',
  },
} as const;

export const unit = (s: string) => (
  <InputAdornment position="end">
    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>{s}</Typography>
  </InputAdornment>
);

export const SectionHeader = ({ label, color }: { label: string; color?: string }) => (
  <Typography variant="overline" sx={{
    fontWeight: 700, color: color ?? 'primary.main', fontSize: '0.7rem', letterSpacing: 0.6, display: 'block', mb: 1,
  }}>
    {label}
  </Typography>
);

/**
 * Small derived-value readout cell ("Avg Diameter — 60.797 mm").
 *
 * When a `formula` is supplied (from the Formula Registry via
 * `useTestFormulas(id).formulaFor(key)`), the LABEL becomes a hover target that
 * shows the exact expression + optional note — the DerivedValue analogue of the
 * grid `FormulaHeaderTooltip`. A subtle dotted underline + help cursor signals
 * that a formula is available. With no `formula`, the label renders exactly as
 * before (no behaviour change → existing call sites are unaffected).
 */
export const DerivedValue = ({
  label,
  value,
  suffix,
  formula,
  note,
}: {
  label: string;
  value: string;
  suffix?: string;
  formula?: string;
  note?: string;
}) => {
  const labelSpan = (
    <Typography
      component="span"
      variant="inherit"
      color="text.secondary"
      sx={formula ? { borderBottom: '1px dotted', borderColor: 'text.disabled', cursor: 'help' } : undefined}
    >
      {label}
    </Typography>
  );
  return (
    <Typography variant="body2" sx={{ fontSize: '0.76rem', display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      {formula ? (
        <FormulaHeaderTooltip label={label} formula={formula} note={note}>
          {labelSpan}
        </FormulaHeaderTooltip>
      ) : (
        labelSpan
      )}
      <Typography component="span" variant="inherit" sx={{ fontWeight: 600 }}>
        {value}{suffix ? ` ${suffix}` : ''}
      </Typography>
    </Typography>
  );
};

export const fmt = (v: number | null | undefined, decimals: number): string =>
  v === null || v === undefined || !Number.isFinite(v) ? '—' : v.toFixed(decimals);
